import moment from 'moment';

import { BatchListItem } from '@e-origin/contracts';
import { BatchStatusEnum, EORIGIN_DATE_FORMAT_WITH_TIME } from '@e-origin/shared';

import { IconFactory } from '@assets';
import { TableActionEnum, TableActions, TableColumnProps } from '@components';
import { VIEW_DETAILS } from '@constants';
import { useUser } from '@hooks/user';
import { useSettings } from '@hooks/useSettings';
import { deleteAllowed } from './helper';

export const useBuildArchivedColumns = ({ handleArchivedTableAction, batchView, currentTab }): TableColumnProps<BatchListItem>[] => {
  const { data: settings } = useSettings();
  const { data: user } = useUser();

  return [
    {
      name: 'Batch ID',
      cell: (row) => row.counter,
      selector: 'counter',
      sortable: true,
    },
    {
      name: 'Batch name',
      cell: (row) => row.name,
      selector: 'name',
      sortable: true,
    },
    {
      name: 'Customer',
      cell: (row) => (row.virtuals as any)?.customerName || '',
    },
    {
      name: 'File name',
      cell: (row) => row.filename,
      selector: 'filename',
      sortable: true,
    },
    {
      name: 'Date',
      cell: (row) => moment(row.updatedAt).format(EORIGIN_DATE_FORMAT_WITH_TIME),
      selector: 'updatedAt',
      sortable: true,
    },
    {
      name: currentTab === BatchStatusEnum.ARCHIVED ? '' : 'Error',
      cell: (row) => row.status.details,
    },
    {
      name: 'Actions',
      cell: (row, index) => {
        const tableActionsList = [];
        if (deleteAllowed(row)) {
          tableActionsList.push({
            icon: IconFactory.delete,
            text: 'Delete',
            onClick: () => handleArchivedTableAction(TableActionEnum.Delete, row),
          });
        }
        tableActionsList.push({
          icon: IconFactory.archive,
          text: 'Unarchive',
          onClick: () => handleArchivedTableAction(TableActionEnum.Archive, row),
        });
        if (row.virtuals.hasFileInCloud) {
          tableActionsList.push({
            icon: IconFactory.download,
            text: 'Download file',
            onClick: () => handleArchivedTableAction(TableActionEnum.DownloadFile, row),
          });
        }
        const secondaryActionsList = [
          {
            icon: IconFactory.view,
            text: VIEW_DETAILS,
            onClick: () => handleArchivedTableAction(TableActionEnum.View, row),
          },
          ...(!(settings?.isForwarder && !user?.isSupervisor)
            ? [
                {
                  icon: IconFactory.riskAnalysisReport,
                  text: 'Generate risk analysis report',
                  onClick: () => handleArchivedTableAction(TableActionEnum.GenerateRiskAnalysisReport, row),
                },
              ]
            : []),
          {
            icon: IconFactory.report,
            text: 'Generate invoice',
            onClick: () => handleArchivedTableAction(TableActionEnum.GenerateInvoice, row),
          },
          {
            icon: IconFactory.viewImage,
            text: 'Export screenshots',
            onClick: () => handleArchivedTableAction(TableActionEnum.ExportScreenshots, row),
          },
          {
            icon: IconFactory.report,
            text: 'Generate customs report',
            onClick: () => handleArchivedTableAction(TableActionEnum.GenerateCustomsReport, row),
          },
        ];
        if (row.canCleanup && !row.canDelete) {
          secondaryActionsList.push({
            icon: IconFactory.cleanup,
            text: 'Cleanup',
            onClick: () => handleArchivedTableAction(TableActionEnum.Cleanup, row),
          });
        }

        return (
          <TableActions
            actions={tableActionsList}
            secondaryActions={secondaryActionsList}
            disableAfterClick={true}
            showDebugMenu={true}
            batchView={batchView}
            data-pw={row.name}
          />
        );
      },
    },
  ];
};
